import {CreateAccessor, RunInAction} from "mobx-graphlink";
import {Session} from "../../../../../../Server/DB/sessions/@Session.js";
import {Annotation, AnnotationsPlugin, PanAndZoomPlugin} from "uplot-vplugins";
import {SessionDataProcessor} from "../../../../../@Shared/Processors/SessionDataProcessor.js";
import {store} from "../../../../../../Store/index.js";
import {Moment_ToDate_WithTimeZonePreApplied, moment_local} from "../../../../../../Utils/General/General.js";
import {uplotDefaults} from "../../../../../../Utils/UI/UPlotDefaults.js";
import {DetailChart_series} from "./DetailChartSeries.js";
import {untracked} from "mobx";

export const GetDetailChartOptions = CreateAccessor((
	session: Session,
	finalWidth: number, height: number,
	minTime_floored: number, maxTime_ceilinged: number,
	//processor: SessionDataProcessor,
	//getExtInfoForRedraw: () => {viewRange_startTime: number, viewRange_endTime: number} | n,
	maxViewRange: number,
	yScale_min: number, yScale_max: number,
	maxEEGActivity: number,
	annotations: Annotation[],
): uPlot.Options=>{
	const uiState = store.main.timeline.sessions;
	return {
		id: `chart_detailPanel`,
		class: "eeg-chart",
		width: finalWidth,
		height,
		plugins: [
			new PanAndZoomPlugin({
				pan_mouseButtons: [0, 1], // left and middle mouse buttons
				zoomFactor_y: 0,
				clamp: true,
				xMin: minTime_floored / 1000,
				xMax: maxTime_ceilinged / 1000,
				xRangeMax: maxViewRange / 1000,
				yMin: yScale_min,
				yMax: yScale_max,
				yRangeMax: null, // no limit on y-range
			}),
			// update view-range based on pan/zoom
			{
				hooks: {
					setScale: (self, scaleKey)=>{
						if (scaleKey == "x") {
							const currentXScale = self.scales.x.IncludeKeys("min", "max");
							RunInAction("EEGChart_updateViewRange", ()=>{
								uiState.viewRange_startTime = currentXScale.min! * 1000;
								uiState.viewRange_endTime = currentXScale.max! * 1000;
							});
							//lastUpdateInfoRef.current?.VSet(getExtInfoForRedraw()?.IncludeKeys("viewRange_startTime", "viewRange_endTime"));
						}
					},
				},
			} as uPlot.Plugin,
			// update visibility/enabledness of series/lines
			{
				hooks: {
					setSeries: (chart, index: number, opts)=>{
						RunInAction("EEGChart_LineVisibilityChanged", ()=>{
							if (opts.show) {
								uiState.disabledSeries.Remove(index);
							} else {
								if (!uiState.disabledSeries.includes(index)) {
									uiState.disabledSeries.push(index);
									uiState.disabledSeries.sort();
								}
							}
						});
					},
				},
			} as uPlot.Plugin,
			new AnnotationsPlugin({
				annotations,
			}),
		].filter(a=>a) as uPlot.Plugin[],
		cursor: {
			drag: {x: false, setScale: false},
		},
		tzDate: rawValue=>{
			const timeInMS = rawValue * 1000;
			const time_creatorLocal = moment_local(timeInMS, session?.localOffsetFromUTC);
			return Moment_ToDate_WithTimeZonePreApplied(time_creatorLocal);
		},
		axes: [
			{
				...uplotDefaults.axes_props,
				size: 50,
			},
			{
				...uplotDefaults.axes_props,
				size: 50,
			},
			// commented; this is when the third axis was used for eeg-activity
			/*{
				...uplotDefaults.axes_props,
				size: 50,
				grid: {show: false},
				scale: "z",
				side: 1,
				//incrs: [1, 5, 10, 50, 100, 500, 1000],
				incrs: [1, 5, 10, 50, 100, 500, 1000],
			},*/
			{
				...uplotDefaults.axes_props,
				size: 50,
				grid: {show: false},
				scale: "percent",
				side: 1,
				incrs: [0, .1, .2, .3, .4, .5, .6, .7, .8, .9, 1],
				label: "Percentage",
				labelSize: 12,
				values: (u, rawValues)=>{
					if (rawValues == null) return [];
					return rawValues.map(rawValue=>{
						return rawValue.ToPercentStr(0);
					})
				}
			},
		],
		scales: {
			x: {
				auto: false,
				// Why do we read these without tracking/establishing a dependency? Because view-range can change really frequently (eg. during pan), so we want to apply those changes manually. (see DetailChart's "Main update effect" block)
				// (Otherwise, a full re-render of the DetailChart comp would be triggered by every view-range change, which causes lag/jank -- and also currently messes up zooming-state, due to it losing the current-mouse-position.)
				min: untracked(()=>uiState.viewRange_startTime) / 1000,
				max: untracked(()=>uiState.viewRange_endTime) / 1000,
			},
			y: {
				auto: false,
				range: (u, dataMin, dataMax)=>{
					//return [-100, 100] as [number, number];

					// todo: get this working (dataMin and dataMax are always null fsr)
					/*console.log("DataMin/Max:", dataMin, dataMax);
					const dataMostExtreme = Math.max(Math.abs(dataMin), Math.abs(dataMax));
					const dataMostExtreme_ceilingedTo5 = dataMostExtreme.CeilingTo(5);
					return [-dataMostExtreme_ceilingedTo5, dataMostExtreme_ceilingedTo5] as [number, number];*/

					//return [-5, 15] as [number, number]; // hardcoded for now, for breath-data

					return [yScale_min, yScale_max] as [number, number];
				},
			},
			// commented; this is when the third axis was used for eeg-activity
			/*z: {
				auto: false,
				range: (u, min, max)=>{
					return [0, u["v_newZMax"] ?? maxEEGActivity];
				},
			},*/
			// new: third axis is used for percentages
			percent: {
				auto: false,
				range: (u, min, max)=>{
					return [0, 1];
				},
			},
		},
		series: DetailChart_series,
	};
});